import { EditUerForm } from 'components/EditUerForm';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { useReadFilesAsBase64 } from 'components/FilesSelector/hooks/useReadFilesAsBase64';
import { ModalDialog } from 'components/ModalDialog';
import { UerChapter, UerClassifier } from 'constantsProject';
import { RequestStatus } from 'features/request/requestSlice';
import type {
    ChapterEnum,
    PriceListUerRowWithRatioData,
    ServiceDataResultOfPriceListUerRowBasisData,
    WorkOrderPriceStatusEnum,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import { PriceConstructionTypeRelationStatusEnum, ProjectMethodologyEnum, UerRecordStatusEnum } from 'gql/graphql';
import { type IUseAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { useContractConstructionTypes } from 'pages/Project/Common/Contract/hooks/useContractConstructionTypes';
import { Button } from 'uikit';
import { formatNumberToRu } from 'utils';

import * as R from 'ramda';
import type { Dispatch, SetStateAction } from 'react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { useCurrentEstimateBasis } from './hooks/useCurrentEstimateBasis';
import { useGetUer } from './hooks/useGetUer';
import { useParseUer } from './hooks/useParseUer';
import { useSaveUer } from './hooks/useSaveUer';

const uerChaptersItems = Object.entries(UerChapter).map(([key, value]) => ({ id: key, label: value }));

const uerClassifierItems = Object.entries(UerClassifier).map(([key, value]) => ({ id: key, label: value }));

interface ICreateOrEditUerProps {
    constructionTypeId?: number;
    onSave: () => void;
    contractId: number | undefined;
    fromWorkOrder?: boolean;
    closeModal?: () => void;
    chapter?: ChapterEnum | null;
    noShowButton?: boolean;
    isUnableToLoadPredicate?: (status?: WorkOrderPriceStatusEnum) => boolean;
    resetPageData?: () => void;
    setCheckedIds?: Dispatch<SetStateAction<Record<string, boolean>>>;
    workOrderStatus?: WorkOrderStatusEnum;
    uerId?: number;
    workOrderPriceId?: number;
    accessRules?: IUseAccess['workOrderChangeUer'];
    catalogVersion?: string;
    catalogName?: string;
    isReadOnly?: boolean;
}

const fieldsToCheck = {
    0: ['code', 'name', 'unit', 'basePrice.unitCost', 'uerClassifier', 'chapter', 'justification'],
    2: ['workKind', 'workInSum', 'workRatio'],
};

export const CreateOrEditUer: React.FC<ICreateOrEditUerProps> = (props) => {
    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const {
        constructionTypeId,
        onSave,
        contractId,
        closeModal,
        chapter,
        noShowButton,
        isUnableToLoadPredicate,
        resetPageData,
        setCheckedIds,
        workOrderStatus,
        accessRules,
        uerId: uerIdProp,
        workOrderPriceId: workOrderPriceIdProp,
        fromWorkOrder,
        catalogVersion,
        catalogName,
        isReadOnly,
    } = props;

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {
        uerId: uerIdParam,
        workOrderPriceId: workOrderPriceIdParam,
        id: projectId,
        methodology,
        orderId,
    } = useParams();

    const uerId = useMemo(() => (uerIdProp ? String(uerIdProp) : uerIdParam), [uerIdParam, uerIdProp]);

    const isMethodologyUER = methodology?.toUpperCase() === ProjectMethodologyEnum.Uer;

    const workOrderPriceId = useMemo(
        () => (workOrderPriceIdProp ? String(workOrderPriceIdProp) : workOrderPriceIdParam),
        [workOrderPriceIdParam, workOrderPriceIdProp],
    );

    const { projectData } = useProjectData(projectId);

    const hasWorkKind = useMemo(
        () => Boolean(projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasWorkKind),
        [projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasWorkKind],
    );

    const defaultChapterItem = useMemo(() => uerChaptersItems.find((c) => c.id === chapter), [chapter]);
    const {
        control,
        handleSubmit,
        reset,
        getValues,
        setValue,
        formState: { errors },
        watch,
    } = useForm();
    const currentFormValues = getValues();
    const uerCode = useWatch({ control, name: 'code' });
    const selectedChapter = useWatch({ control, name: 'chapter' });
    const selectedUerClassifier = useWatch({ control, name: 'uerClassifier' });

    useEffect(() => {
        setValue('chapter', defaultChapterItem);
    }, [defaultChapterItem, setValue]);

    const [checkboxValues, setCheckboxValues] = useState<Record<string, boolean>>(
        constructionTypeId ? { [constructionTypeId]: true } : {},
    );

    const typeTabCount = useMemo(
        () =>
            Object.keys(checkboxValues).reduce(
                (sum, key) => sum + (checkboxValues[key] && currentFormValues[key]?.unitCost ? 1 : 0),
                0,
            ),
        [checkboxValues, currentFormValues],
    );

    const items = useMemo(
        () => [
            { id: 0, label: 'Общие данные' },
            { id: 1, label: 'Виды строительства', count: typeTabCount },
            ...(hasWorkKind ? [{ id: 2, label: 'Вид работ' }] : []),
        ],
        [hasWorkKind, typeTabCount],
    );
    const [isModalOpened, setIsModalOpened] = useState(false);
    const [activeTab, setActiveTab] = useState(items[0]);
    const [selectedFile, setSelectedFile] = useState<any>(null);

    const [contractConstructionTypes] = useContractConstructionTypes(contractId, ProjectMethodologyEnum.Uer);

    const [uerRowData, uerRowDataStatus, resetUerRowHandler, ratioItemsUsedIds] = useGetUer({
        uerId,
        reset,
    });

    useEffect(() => {
        if (uerRowData) {
            const checkedCurrentPrices = uerRowData?.uerPriceListUerRowWithRatio?.data?.currentPrices?.reduce(
                (acc: any, price) => ({
                    ...acc,
                    [price.constructionTypeId]: true,
                }),
                {},
            );

            if (R.isEmpty(checkedCurrentPrices)) {
                if (constructionTypeId) {
                    setCheckboxValues({ [constructionTypeId]: true });
                } else {
                    const linkedConstructionTypes = (
                        uerRowData?.uerPriceListUerRowWithRatio?.data?.constructionTypes || []
                    )
                        .filter((item) => item.isUsedLink)
                        .reduce(
                            (resultObj: { [key: number]: boolean }, item) => ({
                                ...resultObj,
                                [item.id]: true,
                            }),
                            {},
                        );

                    setCheckboxValues((prevChecked) => ({ ...prevChecked, ...linkedConstructionTypes }));
                }
            } else {
                setCheckboxValues((checked) => ({ ...checked, ...checkedCurrentPrices }));
            }
        }
    }, [constructionTypeId, contractConstructionTypes, uerRowData]);

    const { status } = uerRowData?.uerPriceListUerRowWithRatio?.data || {};

    const { currentEstimateBasisRequest, resetCurrentEstimateBasis, currentEstimateBasisData } =
        useCurrentEstimateBasis(setCheckboxValues);

    const {
        uerDataParseRequestSuccess,
        setUerDataParseRequestSuccess,
        parsedUerData,
        parsedUerStatus,
        parsedUerRequest,
        resetParsedDataHandler,
        onCloseParseErrorModal,
        wrongParseUerParameters,
        wrongParseUerParametersData,
        wrongParseUerParametersCreateReport,
    } = useParseUer({
        reset,
        setValue,
        checkboxValues,
        uerRowData: uerRowData?.uerPriceListUerRowWithRatio?.data as PriceListUerRowWithRatioData,
        uerId,
        isMethodologyUER,
        getValues,
        fromWorkOrder,
        catalogVersion,
        catalogName,
    });

    const onCloseCreateUerModal = useCallback(() => {
        onSave();
        setIsModalOpened(false);
    }, [onSave]);

    const resetModalDataExceptSaveData = useCallback(() => {
        setSelectedFile(null);
        resetCurrentEstimateBasis(null);
        resetParsedDataHandler(undefined);
        resetUerRowHandler(undefined);
        setCheckboxValues(constructionTypeId ? { [constructionTypeId]: true } : {});

        const unitCosts = contractConstructionTypes?.reduce((acc: any, type) => {
            acc[type.id] = { unitCostWithOther: null };

            return acc;
        }, {});

        reset({
            chapter: defaultChapterItem,
            code: '',
            justification: '',
            name: '',
            unit: '',
            basePrice: null,
            uerClassifier: null,
            ratioItems: null,
            ratioGroups: null,
            workKind: null,
            workCode: '',
            workUnit: '',
            workInSum: null,
            workRatio: null,
            ...unitCosts,
        });
        setActiveTab(items[0]);
    }, [
        constructionTypeId,
        contractConstructionTypes,
        defaultChapterItem,
        items,
        reset,
        resetCurrentEstimateBasis,
        resetParsedDataHandler,
        resetUerRowHandler,
    ]);

    const onSaveSuccess = useCallback(() => {
        resetModalDataExceptSaveData();
        resetPageData?.();
        onCloseCreateUerModal();
    }, [onCloseCreateUerModal, resetModalDataExceptSaveData, resetPageData]);

    const { onSubmit, saveStatus, saveDataReset } = useSaveUer({
        uerId,
        workOrderPriceId,
        contractId,
        isUerParsed: uerDataParseRequestSuccess,
        uerRecord: uerRowData?.uerPriceListUerRowWithRatio?.data as PriceListUerRowWithRatioData,
        checkboxValues,
        currentEstimateBasisData,
        onCloseModal: closeModal,
        onSaveSuccess,
        onCloseCreateUerModal,
        setCheckedIds,
        reset,
        workOrderStatus,
        parsedUerData: parsedUerData as ServiceDataResultOfPriceListUerRowBasisData,
    });

    const isUnableToLoad = useMemo(
        () => isUnableToLoadPredicate?.(status as unknown as WorkOrderPriceStatusEnum),
        [isUnableToLoadPredicate, status],
    );
    const ratioGroupIdsControl = useMemo(() => ({ control, name: 'ratioGroupIds' }), [control]);
    const ratioIdsControl = useMemo(() => ({ control, name: 'ratioIds' }), [control]);
    const dismantlingCoefControl = useMemo(() => ({ control, name: 'dismantlingCoefIds' }), [control]);
    const areConstructionTypesCheckedAndDisabled = useMemo(
        () =>
            status === UerRecordStatusEnum.ContractCondition ||
            (status === UerRecordStatusEnum.NewRecord && parsedUerStatus !== RequestStatus.SUCCESS),
        [parsedUerStatus, status],
    );

    const isConstructionTypesContractCondition = status === UerRecordStatusEnum.ContractCondition;
    const isConstructionTypesAdditionalCondition = status === UerRecordStatusEnum.AdditionalCondition;

    const areControlsDisabled =
        (!uerId && (parsedUerStatus !== RequestStatus.SUCCESS || !parsedUerData?.result)) ||
        (!!uerId && (parsedUerStatus !== RequestStatus.SUCCESS || !parsedUerData?.result));

    const modalTitle = useMemo(() => {
        if (isReadOnly) {
            return 'Просмотр расценки';
        }
        if (uerId) {
            return 'Редактирование расценки';
        }

        return 'Создание расценки';
    }, [isReadOnly, uerId]);

    const saveButtonTitle = uerId ? 'Сохранить' : 'Создать';

    const base64Files = useReadFilesAsBase64(selectedFile);

    useEffect(() => {
        if (base64Files?.length) {
            dispatch(
                parsedUerRequest({
                    contractId,
                    fileContent: base64Files[0],
                    chapter: getValues().chapter?.id,
                    uerClassifier: getValues().uerClassifier?.id ?? null,
                }),
            );
        }
    }, [base64Files, contractId, dispatch, getValues, parsedUerRequest]);

    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            const isChapterUpdated =
                name === 'chapter' &&
                type === 'change' &&
                selectedChapter?.id &&
                selectedChapter.id !== value.chapter.id;
            const isUerClassifierUpdated =
                name === 'uerClassifier' &&
                type === 'change' &&
                (selectedUerClassifier?.id ?? null) !== (value.uerClassifier?.id ?? null);

            if (!isChapterUpdated && !isUerClassifierUpdated) {
                return;
            }

            const isShouldRequestParsedEstimateBasis =
                base64Files?.length && !isUnableToLoad && !isConstructionTypesAdditionalCondition;
            const isShouldRequestCurrentEstimateBasis =
                isChapterUpdated &&
                (isUnableToLoad ||
                    isConstructionTypesAdditionalCondition ||
                    uerRowData?.uerPriceListUerRowWithRatio?.data?.status === UerRecordStatusEnum.NewRecord);

            if (isShouldRequestParsedEstimateBasis) {
                dispatch(
                    parsedUerRequest({
                        contractId,
                        fileContent: base64Files[0],
                        chapter: value.chapter?.id,
                        uerClassifier: value.uerClassifier?.id ?? null,
                    }),
                );
            } else if (isShouldRequestCurrentEstimateBasis) {
                dispatch(
                    currentEstimateBasisRequest({
                        uerBaseRowId: Number(uerId),
                        chapter: value.chapter.id,
                    }),
                );
            }
        });

        return () => subscription.unsubscribe();
    }, [
        base64Files,
        contractId,
        currentEstimateBasisRequest,
        dispatch,
        isConstructionTypesAdditionalCondition,
        isUnableToLoad,
        parsedUerRequest,
        selectedChapter,
        selectedUerClassifier,
        uerId,
        uerRowData?.uerPriceListUerRowWithRatio?.data?.status,
        watch,
    ]);

    useEffect(
        () => () => {
            resetModalDataExceptSaveData();
            saveDataReset(undefined);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const onTabChange = useCallback(({ value }) => setActiveTab(value), [setActiveTab]);

    const onOpenModal = useCallback(() => {
        setIsModalOpened(true);

        sendGpnAnalyticsEvent({
            componentId: 'rim_create price button_pressed',
            projectId,
            orderId: orderId ? Number(orderId) : undefined,
            orderStatus: projectData?.contractNavigation?.data?.workOrderNavs?.find((w) => w.id === Number(orderId))
                ?.status,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
            methodology: projectData?.methodology,
        });
    }, [
        orderId,
        projectData?.contractNavigation?.data?.workOrderNavs,
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectId,
        sendGpnAnalyticsEvent,
    ]);

    const oncloseModal = useCallback(() => {
        resetModalDataExceptSaveData();
        saveDataReset(undefined);
        setIsModalOpened(false);

        if (closeModal) {
            closeModal();
        } else if (uerId) {
            navigate('../');
        }
    }, [closeModal, navigate, resetModalDataExceptSaveData, saveDataReset, uerId]);

    const onUploadUerFile = useCallback(
        (files) => {
            if (files.length && contractId) {
                setUerDataParseRequestSuccess(false);
                setSelectedFile(files);
            } else if (!files.length && selectedFile?.length) {
                setSelectedFile([]);
                resetParsedDataHandler(undefined);
            }
        },
        [contractId, selectedFile, setUerDataParseRequestSuccess, resetParsedDataHandler],
    );

    const onConstructionTypeCheckChange = useCallback(
        (id: number) =>
            ({ checked }: { checked: boolean }) => {
                if (
                    checked &&
                    (isUnableToLoad ||
                        isConstructionTypesAdditionalCondition ||
                        (uerRowData?.uerPriceListUerRowWithRatio?.data?.status === UerRecordStatusEnum.NewRecord &&
                            !base64Files?.[0])) &&
                    currentFormValues?.chapter?.id
                ) {
                    dispatch(
                        currentEstimateBasisRequest({
                            uerBaseRowId: Number(uerId),
                            chapter: currentFormValues?.chapter?.id,
                        }),
                    );
                } else {
                    setValue(
                        `${id}.unitCostWithOther`,
                        parsedUerData?.data?.currentPrices?.filter((price) => price?.constructionTypeId === id)[0]
                            ?.unitCostWithOther || '',
                    );
                }
                setCheckboxValues((prev: Record<number, boolean>) => ({ ...prev, [id]: checked }));
            },
        [
            base64Files,
            currentEstimateBasisRequest,
            currentFormValues?.chapter?.id,
            dispatch,
            isConstructionTypesAdditionalCondition,
            isUnableToLoad,
            parsedUerData?.data?.currentPrices,
            setValue,
            uerId,
            uerRowData?.uerPriceListUerRowWithRatio?.data?.status,
        ],
    );

    useEffect(() => {
        if (currentEstimateBasisData?.result) {
            const idsCurrentPriceContractCondition =
                uerRowData?.uerPriceListUerRowWithRatio?.data?.currentPrices?.reduce((acc: number[], item) => {
                    if (
                        item.constructionTypeRelationStatus ===
                        PriceConstructionTypeRelationStatusEnum.ContractCondition
                    ) {
                        acc = [...acc, item.constructionTypeId];
                    }

                    return acc;
                }, []);

            const checkedIdConstructionTypes = Object.entries(checkboxValues).reduce((acc: any, [key, value]) => {
                if (value && !idsCurrentPriceContractCondition?.includes(Number(key))) {
                    acc = [...acc, key];
                }

                return acc;
            }, []);

            checkedIdConstructionTypes.forEach((id: string) => {
                setValue(
                    `${id}.unitCostWithOther`,
                    formatNumberToRu(
                        currentEstimateBasisData?.data?.currentPrices.find(
                            (currentPriceItem) => currentPriceItem.constructionTypeId === Number(id),
                        )?.unitCostWithOther,
                        2,
                    ),
                );
            });
        }
    }, [
        checkboxValues,
        currentEstimateBasisData?.data?.currentPrices,
        currentEstimateBasisData?.result,
        setValue,
        uerRowData?.uerPriceListUerRowWithRatio?.data?.currentPrices,
    ]);

    const disabledByConstructionType = useCallback(
        (id: any) =>
            (isConstructionTypesContractCondition || isConstructionTypesAdditionalCondition) &&
            (currentFormValues[id]?.constructionTypeRelationStatus ===
                PriceConstructionTypeRelationStatusEnum.AdditionalCondition ||
                currentFormValues[id]?.constructionTypeRelationStatus ===
                    PriceConstructionTypeRelationStatusEnum.ContractCondition ||
                constructionTypeId === id),
        [
            constructionTypeId,
            currentFormValues,
            isConstructionTypesAdditionalCondition,
            isConstructionTypesContractCondition,
        ],
    );

    const isDisableIfCreateNotFile = useMemo(
        () => !uerId && !parsedUerData?.data?.code,
        [parsedUerData?.data?.code, uerId],
    );

    const isDisableIfDraftNotFile = useMemo(
        () => !!uerId && !!workOrderPriceId && !parsedUerData?.data,
        [parsedUerData?.data, uerId, workOrderPriceId],
    );

    const isDisableIfEditNotPrice = useMemo(
        () =>
            !!uerId &&
            !workOrderPriceId &&
            (status === UerRecordStatusEnum.Draft || status === UerRecordStatusEnum.NewRecord) &&
            !parsedUerData?.data,
        [parsedUerData?.data, status, uerId, workOrderPriceId],
    );

    const errorsKeys = Object.keys(errors);
    const btnConfirmDisabled = useMemo(() => {
        const isEmptyCheckboxValues = Object.entries(checkboxValues).reduce(
            (acc: any, [key, value]) => acc && (!value || R.isNil(currentFormValues[key]?.unitCostWithOther)),
            true,
        );

        const isHasNotSelected =
            uerRowData?.uerPriceListUerRowWithRatio?.data?.currentPrices.length === contractConstructionTypes.length;

        return (
            !!errorsKeys.length ||
            isEmptyCheckboxValues ||
            (!!projectData?.contractNavigation?.data?.workOrderNavs?.length &&
                isConstructionTypesContractCondition &&
                isHasNotSelected &&
                !hasWorkKind)
        );
    }, [
        checkboxValues,
        contractConstructionTypes.length,
        currentFormValues,
        errorsKeys.length,
        hasWorkKind,
        isConstructionTypesContractCondition,
        projectData?.contractNavigation?.data?.workOrderNavs?.length,
        uerRowData?.uerPriceListUerRowWithRatio?.data?.currentPrices.length,
    ]);

    const checkIsConstructionTypeEditable = useCallback(
        (id) => constructionTypeId === id || checkboxValues[id] || areConstructionTypesCheckedAndDisabled,
        [areConstructionTypesCheckedAndDisabled, checkboxValues, constructionTypeId],
    );

    const checkIsConstructionTypeCheckboxDisabled = useCallback(
        (id) => {
            const isUsedLink = uerRowData?.uerPriceListUerRowWithRatio?.data?.constructionTypes.filter(
                (type) => type.id === id,
            )[0]?.isUsedLink;

            if (uerRowData?.uerPriceListUerRowWithRatio?.data?.status === UerRecordStatusEnum.NewRecord) {
                return !!isUsedLink || constructionTypeId === id;
            }

            return (
                disabledByConstructionType(id) ||
                constructionTypeId === id ||
                isDisableIfCreateNotFile ||
                isDisableIfDraftNotFile ||
                isUsedLink ||
                isDisableIfEditNotPrice
            );
        },

        [
            constructionTypeId,
            disabledByConstructionType,
            isDisableIfCreateNotFile,
            isDisableIfDraftNotFile,
            isDisableIfEditNotPrice,
            uerRowData?.uerPriceListUerRowWithRatio?.data?.constructionTypes,
            uerRowData?.uerPriceListUerRowWithRatio?.data?.status,
        ],
    );

    const checkIsConstructionTypeCheckboxChecked = useCallback(
        (id) =>
            checkboxValues[id] ||
            (currentFormValues[id]?.unitCostWithOther &&
                (areConstructionTypesCheckedAndDisabled || constructionTypeId === id || checkboxValues[id])),
        [areConstructionTypesCheckedAndDisabled, checkboxValues, constructionTypeId, currentFormValues],
    );

    const isChapterFieldDisabled = useMemo(
        () =>
            !uerId ||
            (!!uerId && !isUnableToLoad && !isConstructionTypesAdditionalCondition && !uerDataParseRequestSuccess) ||
            (!!projectData?.contractNavigation?.data?.workOrderNavs?.length && isConstructionTypesContractCondition),
        [
            isConstructionTypesAdditionalCondition,
            isConstructionTypesContractCondition,
            isUnableToLoad,
            projectData?.contractNavigation?.data?.workOrderNavs?.length,
            uerDataParseRequestSuccess,
            uerId,
        ],
    );

    const isUerClassifierFieldDisabled = useMemo(
        () =>
            (!isUnableToLoad && !isConstructionTypesAdditionalCondition && areControlsDisabled) ||
            (!!projectData?.contractNavigation?.data?.workOrderNavs?.length && isConstructionTypesContractCondition),
        [
            areControlsDisabled,
            isConstructionTypesAdditionalCondition,
            isConstructionTypesContractCondition,
            isUnableToLoad,
            projectData?.contractNavigation?.data?.workOrderNavs?.length,
        ],
    );

    const isAccessRulesEnabled = Boolean(accessRules);
    const isDisabledCreateQuote = isAccessRulesEnabled && !accessRules?.canManualCreation;

    const projectHasPriceListRatio = useMemo(
        () => projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasPriceListRatio ?? true,
        [projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasPriceListRatio],
    );

    return (
        <>
            {!noShowButton && (
                <Button
                    disabled={isDisabledCreateQuote}
                    label="Создать расценку"
                    view="secondary"
                    size="s"
                    onClick={onOpenModal}
                />
            )}
            <ModalDialog
                isOpen={noShowButton || isModalOpened}
                title={modalTitle}
                onConfirm={isReadOnly ? undefined : handleSubmit(onSubmit)}
                onClose={oncloseModal}
                withTitleCloseButton
                btnConfirmLabel={saveButtonTitle}
                btnConfirmDisabled={btnConfirmDisabled}
                isCommit={saveStatus === RequestStatus.FETCHING || parsedUerStatus === RequestStatus.FETCHING}
                style={{ width: '600px' }}
                isOnClickOutsideDisable
            >
                <EditUerForm
                    isDataFetching={uerRowDataStatus === RequestStatus.FETCHING}
                    isUnableToLoad={isUnableToLoad}
                    uerId={uerId}
                    isFileSelectorVisible={!isConstructionTypesAdditionalCondition}
                    isFileSelectorLoading={parsedUerStatus === RequestStatus.FETCHING}
                    onFileSelectorFilesChange={onUploadUerFile}
                    activeTab={activeTab}
                    onTabChange={onTabChange}
                    tabItems={items}
                    control={control}
                    fieldsToCheck={fieldsToCheck}
                    uerClassifierItems={uerClassifierItems}
                    isUerClassifierFieldDisabled={isUerClassifierFieldDisabled}
                    uerChaptersItems={uerChaptersItems}
                    isChapterFieldDisabled={isChapterFieldDisabled}
                    areControlsDisabled={areControlsDisabled}
                    contractConstructionTypes={contractConstructionTypes}
                    checkIsConstructionTypeCheckboxCheckedHandler={checkIsConstructionTypeCheckboxChecked}
                    onConstructionTypeCheckChange={onConstructionTypeCheckChange}
                    checkIsConstructionTypeCheckboxDisabledHandler={checkIsConstructionTypeCheckboxDisabled}
                    checkIsConstructionTypeEditableHandler={checkIsConstructionTypeEditable}
                    ratioGroupIdsControl={ratioGroupIdsControl}
                    ratioIdsControl={ratioIdsControl}
                    dismantlingCoefControl={dismantlingCoefControl}
                    uerCode={uerCode}
                    ratioItemsUsedIds={ratioItemsUsedIds}
                    selectedFile={selectedFile}
                    hasPriceListRatio={projectHasPriceListRatio}
                    setValue={setValue}
                    hasWorkKind={hasWorkKind}
                />
                <ErrorHandlingModal
                    titleText="Проверка Сметы-Основания"
                    isSubmitted={wrongParseUerParameters}
                    statusRequest={parsedUerStatus}
                    dataRequest={wrongParseUerParametersData}
                    errorsRequest={[]}
                    handleCloseModalError={onCloseParseErrorModal}
                    handleCreateReport={wrongParseUerParametersCreateReport}
                />
            </ModalDialog>
        </>
    );
};

CreateOrEditUer.displayName = 'CreateOrEditUer';
